// based on https://developers.google.com/web/fundamentals/design-and-ux/input/touch#implement-custom-gestures

import {RefObject, useCallback, useEffect, useRef} from "react";

import {useStateRef} from "@pg-mono/hooks";

import {GESTURE_SWIPE_DEADBAND} from "../constants/gesture";

export enum GestureDirection {
    LEFT = 1,
    NONE = 0,
    RIGHT = -1
}

export const useSliderTouchEvents = <T extends HTMLElement | null>(
    ref: RefObject<T>,
    onGestureStart: (width?: number) => void,
    onGestureEnd: (gestureDirection: GestureDirection) => void,
    isMobile: boolean,
    isSwipeDisabled: () => boolean = () => false,
    swipeDisabled = false,
    swipeDeadband: number = GESTURE_SWIPE_DEADBAND.DEFAULT
) => {
    const firstTouchPosition = useRef<number>(0);
    const lastTouchPositionRef = useRef<number>(0);
    const swipeDeadbandRef = useRef(swipeDeadband);

    // Sync in an effect (not during render) so the ref reflects committed state under React 19.
    useEffect(() => {
        swipeDeadbandRef.current = swipeDeadband;
    }, [swipeDeadband]);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [shouldBlockEvents, setShouldBlockEvents, shouldBlockEventsRef] = useStateRef(false);
    const gestureDirectionRef = useRef<GestureDirection>(GestureDirection.NONE);
    const isSwipeDetectedRef = useRef(false);
    // True once the gesture moved past the deadband (a real drag, not a tap).
    const hasExceededDeadbandRef = useRef(false);
    // Track active pointer ids to detect multi-touch (e.g. pinch). PointerEvent has no `.touches`,
    // so the legacy `touches.length` guard never fires for pointers — we count ids ourselves.
    const activePointersRef = useRef<Set<number>>(new Set());
    const isMultiTouchRef = useRef(false);

    /*
     * define event handlers
     */

    // start gesture
    const handleGestureStart = useCallback((event: TouchEvent | PointerEvent | MouseEvent) => {
        // preventDefault on pointerdown breaks the pointermove stream on iOS (with touch-action: pan-y),
        // so skip it for PointerEvent and keep it only on the legacy touch path.
        if (!(event instanceof PointerEvent)) event.preventDefault();

        // Multi-touch (pinch) detection — cancel any in-progress swipe once a second pointer/touch lands.
        if (event instanceof PointerEvent) {
            activePointersRef.current.add(event.pointerId);
            if (activePointersRef.current.size > 1) {
                isMultiTouchRef.current = true;
                isSwipeDetectedRef.current = false;
                return;
            }
        }
        if ((event as TouchEvent).touches?.length > 1) {
            isMultiTouchRef.current = true;
            isSwipeDetectedRef.current = false;
            return;
        }

        onGestureStart((event.currentTarget as T)?.clientWidth);
        firstTouchPosition.current = getGesturePosFromEvent(event);

        // reset new gesture
        lastTouchPositionRef.current = getGesturePosFromEvent(event);
        isSwipeDetectedRef.current = true;
        hasExceededDeadbandRef.current = false;
    }, []);

    // move gesture
    const handleGestureMove = useCallback((event: TouchEvent | PointerEvent | MouseEvent) => {
        // While two or more pointers are down (pinch), never treat movement as a swipe.
        if (isMultiTouchRef.current) return;

        lastTouchPositionRef.current = getGesturePosFromEvent(event);

        const differenceInX =
            Number.isFinite(firstTouchPosition.current) && lastTouchPositionRef.current ? firstTouchPosition.current - lastTouchPositionRef.current : 0;

        const deadband = swipeDeadbandRef.current;

        gestureDirectionRef.current =
            differenceInX > deadband ? GestureDirection.LEFT : Math.abs(differenceInX) < deadband ? GestureDirection.NONE : GestureDirection.RIGHT;

        // Only preventDefault/block clicks once past the deadband. Doing it on every micro-movement
        // would make real touchscreens suppress the tap's synthetic click (arrows, tap-to-detail).
        if (gestureDirectionRef.current !== GestureDirection.NONE) {
            if (!hasExceededDeadbandRef.current) setShouldBlockEvents(true);
            hasExceededDeadbandRef.current = true;
            event.preventDefault();
        }

        if (isSwipeDetectedRef.current && gestureDirectionRef.current && !isSwipeDisabled()) {
            onGestureEnd(gestureDirectionRef.current);
            gestureDirectionRef.current = GestureDirection.NONE;
            isSwipeDetectedRef.current = false;
        }
    }, []);

    // end gesture
    const handleGestureEnd = useCallback((event: TouchEvent | PointerEvent | MouseEvent) => {
        // Release the pointer and clear multi-touch once every finger has lifted, so the next single swipe works.
        if (event instanceof PointerEvent) {
            activePointersRef.current.delete(event.pointerId);
            if (activePointersRef.current.size === 0) isMultiTouchRef.current = false;
        }

        // Suppress the trailing synthetic click only after a real drag, so a plain tap still clicks.
        if (hasExceededDeadbandRef.current) event.preventDefault();
        if ((event as TouchEvent).touches && (event as TouchEvent).touches.length > 0) return;

        // Legacy touch path: reset multi-touch when the last touch lifts.
        if (isMultiTouchRef.current && !(event instanceof PointerEvent)) isMultiTouchRef.current = false;

        setShouldBlockEvents(false);
    }, []);

    /*
     * attach/detach event listeners
     */
    useEffect(() => {
        if (!swipeDisabled) {
            activePointersRef.current.clear();
            isMultiTouchRef.current = false;
            isSwipeDetectedRef.current = false;
            hasExceededDeadbandRef.current = false;
        }
    }, [swipeDisabled]);

    useEffect(() => {
        if (!ref.current || !isMobile) return;

        // Check if pointer events are supported.
        if (window.PointerEvent) {
            // Add Pointer Event Listener
            ref.current.addEventListener("pointerdown", handleGestureStart, true);
            ref.current.addEventListener("pointermove", handleGestureMove, true);
            ref.current.addEventListener("pointerup", handleGestureEnd, true);
            ref.current.addEventListener("pointercancel", handleGestureEnd, true);
        } else {
            // Add Touch Listener
            ref.current.addEventListener("touchstart", handleGestureStart, true);
            ref.current.addEventListener("touchmove", handleGestureMove, true);
            ref.current.addEventListener("touchend", handleGestureEnd, true);
            ref.current.addEventListener("touchcancel", handleGestureEnd, true);
            // Add Mouse Listener
            ref.current.addEventListener("mousedown", handleGestureStart, true);
        }

        return () => {
            if (!ref.current || !isMobile) return;

            // unmount event cleanup
            if (window.PointerEvent) {
                ref.current.removeEventListener("pointerdown", handleGestureStart, true);
                ref.current.removeEventListener("pointermove", handleGestureMove, true);
                ref.current.removeEventListener("pointerup", handleGestureEnd, true);
                ref.current.removeEventListener("pointercancel", handleGestureEnd, true);
            } else {
                ref.current.removeEventListener("touchstart", handleGestureStart, true);
                ref.current.removeEventListener("touchmove", handleGestureMove, true);
                ref.current.removeEventListener("touchend", handleGestureEnd, true);
                ref.current.removeEventListener("touchcancel", handleGestureEnd, true);

                ref.current.removeEventListener("mousedown", handleGestureStart, true);
            }
        };
    }, [isMobile]);

    return {shouldBlockEventsRef};
};

export function getGesturePosFromEvent(e: TouchEvent | MouseEvent | PointerEvent) {
    let xPos: number;

    if ((e as TouchEvent).targetTouches) {
        xPos = (e as TouchEvent).targetTouches[0].clientX;
    } else {
        xPos = (e as MouseEvent | PointerEvent).clientX;
    }
    return xPos;
}
// based on https://developers.google.com/web/fundamentals/design-and-ux/input/touch#implement-custom-gestures

import {RefObject, useCallback, useEffect, useRef} from "react";

import {useStateRef} from "@pg-mono/hooks";

import {GESTURE_SWIPE_DEADBAND} from "../constants/gesture";

export enum GestureDirection {
    LEFT = 1,
    NONE = 0,
    RIGHT = -1
}

export const useSliderTouchEvents = <T extends HTMLElement | null>(
    ref: RefObject<T>,
    onGestureStart: (width?: number) => void,
    onGestureEnd: (gestureDirection: GestureDirection) => void,
    isMobile: boolean,
    isSwipeDisabled: () => boolean = () => false,
    swipeDisabled = false,
    swipeDeadband: number = GESTURE_SWIPE_DEADBAND.DEFAULT
) => {
    const firstTouchPosition = useRef<number>(0);
    const lastTouchPositionRef = useRef<number>(0);
    const swipeDeadbandRef = useRef(swipeDeadband);

    // Sync in an effect (not during render) so the ref reflects committed state under React 19.
    useEffect(() => {
        swipeDeadbandRef.current = swipeDeadband;
    }, [swipeDeadband]);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [shouldBlockEvents, setShouldBlockEvents, shouldBlockEventsRef] = useStateRef(false);
    const gestureDirectionRef = useRef<GestureDirection>(GestureDirection.NONE);
    const isSwipeDetectedRef = useRef(false);
    // True once the gesture moved past the deadband (a real drag, not a tap).
    const hasExceededDeadbandRef = useRef(false);
    // Track active pointer ids to detect multi-touch (e.g. pinch). PointerEvent has no `.touches`,
    // so the legacy `touches.length` guard never fires for pointers — we count ids ourselves.
    const activePointersRef = useRef<Set<number>>(new Set());
    const isMultiTouchRef = useRef(false);

    /*
     * define event handlers
     */

    // start gesture
    const handleGestureStart = useCallback((event: TouchEvent | PointerEvent | MouseEvent) => {
        // preventDefault on pointerdown breaks the pointermove stream on iOS (with touch-action: pan-y),
        // so skip it for PointerEvent and keep it only on the legacy touch path.
        if (!(event instanceof PointerEvent)) event.preventDefault();

        // Multi-touch (pinch) detection — cancel any in-progress swipe once a second pointer/touch lands.
        if (event instanceof PointerEvent) {
            activePointersRef.current.add(event.pointerId);
            if (activePointersRef.current.size > 1) {
                isMultiTouchRef.current = true;
                isSwipeDetectedRef.current = false;
                return;
            }
        }
        if ((event as TouchEvent).touches?.length > 1) {
            isMultiTouchRef.current = true;
            isSwipeDetectedRef.current = false;
            return;
        }

        onGestureStart((event.currentTarget as T)?.clientWidth);
        firstTouchPosition.current = getGesturePosFromEvent(event);

        // reset new gesture
        lastTouchPositionRef.current = getGesturePosFromEvent(event);
        isSwipeDetectedRef.current = true;
        hasExceededDeadbandRef.current = false;
    }, []);

    // move gesture
    const handleGestureMove = useCallback((event: TouchEvent | PointerEvent | MouseEvent) => {
        // While two or more pointers are down (pinch), never treat movement as a swipe.
        if (isMultiTouchRef.current) return;

        lastTouchPositionRef.current = getGesturePosFromEvent(event);

        const differenceInX =
            Number.isFinite(firstTouchPosition.current) && lastTouchPositionRef.current ? firstTouchPosition.current - lastTouchPositionRef.current : 0;

        const deadband = swipeDeadbandRef.current;

        gestureDirectionRef.current =
            differenceInX > deadband ? GestureDirection.LEFT : Math.abs(differenceInX) < deadband ? GestureDirection.NONE : GestureDirection.RIGHT;

        // Only preventDefault/block clicks once past the deadband. Doing it on every micro-movement
        // would make real touchscreens suppress the tap's synthetic click (arrows, tap-to-detail).
        if (gestureDirectionRef.current !== GestureDirection.NONE) {
            if (!hasExceededDeadbandRef.current) setShouldBlockEvents(true);
            hasExceededDeadbandRef.current = true;
            event.preventDefault();
        }

        if (isSwipeDetectedRef.current && gestureDirectionRef.current && !isSwipeDisabled()) {
            onGestureEnd(gestureDirectionRef.current);
            gestureDirectionRef.current = GestureDirection.NONE;
            isSwipeDetectedRef.current = false;
        }
    }, []);

    // end gesture
    const handleGestureEnd = useCallback((event: TouchEvent | PointerEvent | MouseEvent) => {
        // Release the pointer and clear multi-touch once every finger has lifted, so the next single swipe works.
        if (event instanceof PointerEvent) {
            activePointersRef.current.delete(event.pointerId);
            if (activePointersRef.current.size === 0) isMultiTouchRef.current = false;
        }

        // Suppress the trailing synthetic click only after a real drag, so a plain tap still clicks.
        if (hasExceededDeadbandRef.current) event.preventDefault();
        if ((event as TouchEvent).touches && (event as TouchEvent).touches.length > 0) return;

        // Legacy touch path: reset multi-touch when the last touch lifts.
        if (isMultiTouchRef.current && !(event instanceof PointerEvent)) isMultiTouchRef.current = false;

        setShouldBlockEvents(false);
    }, []);

    /*
     * attach/detach event listeners
     */
    useEffect(() => {
        if (!swipeDisabled) {
            activePointersRef.current.clear();
            isMultiTouchRef.current = false;
            isSwipeDetectedRef.current = false;
            hasExceededDeadbandRef.current = false;
        }
    }, [swipeDisabled]);

    useEffect(() => {
        if (!ref.current || !isMobile) return;

        // Check if pointer events are supported.
        if (window.PointerEvent) {
            // Add Pointer Event Listener
            ref.current.addEventListener("pointerdown", handleGestureStart, true);
            ref.current.addEventListener("pointermove", handleGestureMove, true);
            ref.current.addEventListener("pointerup", handleGestureEnd, true);
            ref.current.addEventListener("pointercancel", handleGestureEnd, true);
        } else {
            // Add Touch Listener
            ref.current.addEventListener("touchstart", handleGestureStart, true);
            ref.current.addEventListener("touchmove", handleGestureMove, true);
            ref.current.addEventListener("touchend", handleGestureEnd, true);
            ref.current.addEventListener("touchcancel", handleGestureEnd, true);
            // Add Mouse Listener
            ref.current.addEventListener("mousedown", handleGestureStart, true);
        }

        return () => {
            if (!ref.current || !isMobile) return;

            // unmount event cleanup
            if (window.PointerEvent) {
                ref.current.removeEventListener("pointerdown", handleGestureStart, true);
                ref.current.removeEventListener("pointermove", handleGestureMove, true);
                ref.current.removeEventListener("pointerup", handleGestureEnd, true);
                ref.current.removeEventListener("pointercancel", handleGestureEnd, true);
            } else {
                ref.current.removeEventListener("touchstart", handleGestureStart, true);
                ref.current.removeEventListener("touchmove", handleGestureMove, true);
                ref.current.removeEventListener("touchend", handleGestureEnd, true);
                ref.current.removeEventListener("touchcancel", handleGestureEnd, true);

                ref.current.removeEventListener("mousedown", handleGestureStart, true);
            }
        };
    }, [isMobile]);

    return {shouldBlockEventsRef};
};

export function getGesturePosFromEvent(e: TouchEvent | MouseEvent | PointerEvent) {
    let xPos: number;

    if ((e as TouchEvent).targetTouches) {
        xPos = (e as TouchEvent).targetTouches[0].clientX;
    } else {
        xPos = (e as MouseEvent | PointerEvent).clientX;
    }
    return xPos;
}
